// Order maths for the pricing pages (/pricing-2026, /pricing-changes-2026): pure, integer
// minor-unit arithmetic so every worked example is exact. Plan rates and card fees come from
// src/content/pricing/.
//
// Photohawk takes one commission per plan (Lite 16%, Pro 14%, Max 12%), with no fee per order.
// The card fee in the examples is the payment provider's (Stripe's on the photographer's own
// account, or the processing fee where Photohawk processes the payment); it's shown only so the
// "you keep" figure is realistic.

import { WORKED_EXAMPLE } from "@/content/pricing/plans";

export interface Currency {
  /** Printed before the amount, e.g. "£". */
  prefix: string;
  /** Decimal places the currency uses. */
  digits: number;
}

export interface CardFee {
  /** Share of the order, e.g. 0.015 for 1.5%. */
  percent: number;
  /** Fixed part, in major units, e.g. 0.2 for 20p. */
  fixed: number;
}

const scale = (c: Currency) => 10 ** c.digits;
const toMinor = (amount: number, c: Currency) => Math.round(amount * scale(c));
/** Rates as whole basis points, so `minor * bps / 10000` stays exact before rounding. */
const bps = (rate: number) => Math.round(rate * 10000);
const ofOrder = (orderMinor: number, rate: number) => Math.round((orderMinor * bps(rate)) / 10000);

/** "£12.77", "−£0.11", "COP 74,598". `locale` sets digit grouping (en-GB by default). */
export function formatMoney(minor: number, c: Currency = WORKED_EXAMPLE.currency, locale = "en-GB"): string {
  const n = new Intl.NumberFormat(locale, { minimumFractionDigits: c.digits, maximumFractionDigits: c.digits }).format(
    Math.abs(minor) / scale(c),
  );
  return `${minor < 0 ? "−" : ""}${c.prefix}${n}`;
}

/** 0.12 -> "12%", 0.185 -> "18.5%". `locale` sets the decimal mark. */
export const ratePercent = (rate: number, locale = "en-GB") =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(Math.round(rate * 1000) / 10)}%`;

export interface OrderBreakdown {
  order: number;
  /** Photohawk's commission. */
  commission: number;
  /** The payment provider's card fee. */
  cardFee: number;
  keep: number;
}

/**
 * Split an order (major units) into commission at `rate` (the plan's), card fee and what the
 * photographer keeps. All in minor units.
 */
export function orderBreakdown(
  orderAmount: number,
  rate: number,
  cardFee: CardFee = WORKED_EXAMPLE.cardFee,
  c: Currency = WORKED_EXAMPLE.currency,
): OrderBreakdown {
  const order = Math.max(0, toMinor(orderAmount, c));
  if (order === 0) return { order: 0, commission: 0, cardFee: 0, keep: 0 };
  const commission = ofOrder(order, rate);
  const card = ofOrder(order, cardFee.percent) + toMinor(cardFee.fixed, c);
  return { order, commission, cardFee: card, keep: order - commission - card };
}
